import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { SESSION_STORAGE_KEY, type AdminNavigationState } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import {
  useInactiveRestaurantMembershipCount,
  useRestaurantMemberships,
} from "@/rc/hooks/restaurant/use-restaurant";
import { useRestaurantStore } from "@/stores/restaurantStore";
import type { RestaurantMembership } from "@/stores/restaurantStore";
import { Spinner } from "@/components/ui/spinner";
import { NoMembershipScreen } from "./NoMembershipScreen";
import { RestaurantPicker } from "./RestaurantPicker";
import { useProfile } from "@/rc/hooks/profile/use-profile";
import { getDefaultLandingForRole } from "@/lib/auth/routePermissions";
import { FontProvider } from "../FontProvider";

type RestaurantPhase =
  | { kind: "loading" | "empty" | "resolved" }
  | { kind: "picking"; memberships: RestaurantMembership[] };
/**
 * Layer 2: resolves restaurant + role from user_roles into the store.
 */
export function RestaurantGuard() {
  const { pathname, state: locationState } = useLocation();
  const { user, status } = useAuth();
  const { isSuperAdmin, isPending: isProfilePending } = useProfile();
  const {
    data: memberships = [],
    isPending: isMembershipsPending,
    isError: hasDirectMembershipsError,
  } = useRestaurantMemberships(user?.id, isSuperAdmin);

  const [phase, setPhase] = useState<RestaurantPhase>({ kind: "loading" });
  const {
    activeRestaurantId,
    activeRole,
    activeMembershipId,
    setRestaurantState,
    clearRestaurantState,
  } = useRestaurantStore();

  const membershipsLoading = isProfilePending || isMembershipsPending;

  const membershipsError = hasDirectMembershipsError;
  const isNoActiveMemberships = !membershipsLoading && memberships.length === 0;

  const { data: inactiveMembership, isLoading: isInactiveCountLoading } =
    useInactiveRestaurantMembershipCount(
      user?.id,
      isNoActiveMemberships && !isSuperAdmin
    );

  const applyMembership = useCallback(
    (m: RestaurantMembership, all: RestaurantMembership[]) => {
      if (activeMembershipId === m.id) {
        setPhase({ kind: "resolved" });
        return;
      }
      setRestaurantState({
        activeRestaurantId: m.restaurantId,
        activeRole: m.role,
        activeSlug: m.slug,
        activeMembershipId: m.id,
        memberships: all,
      });
      setPhase({ kind: "resolved" });
    },
    [setRestaurantState, activeMembershipId]
  );

  useEffect(() => {
    if (status !== "authenticated" || !user) return;
    if (membershipsLoading) return;

    if (memberships.length === 0) {
      clearRestaurantState();
      setPhase({ kind: "empty" });
      return;
    }

    if (memberships.length === 1) {
      applyMembership(memberships[0], memberships);
      return;
    }

    const savedRestaurantId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const savedMembership = savedRestaurantId
      ? memberships.find((x) => x.restaurantId === savedRestaurantId)
      : undefined;
    if (savedMembership) {
      applyMembership(savedMembership, memberships);
      return;
    }
    clearRestaurantState();
    setPhase({ kind: "picking", memberships });
  }, [
    status,
    user,
    membershipsLoading,
    memberships,
    applyMembership,
    clearRestaurantState,
  ]);

  const handlePick = (m: RestaurantMembership) => {
    applyMembership(m, memberships);
  };

  if (status !== "authenticated" || !user) {
    return null;
  }

  if (phase.kind === "loading" || membershipsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="text-muted-foreground" />
      </div>
    );
  }

  if (membershipsError) {
    return <NoMembershipScreen />;
  }

  if (phase.kind === "empty") {
    if (isInactiveCountLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Spinner className="text-muted-foreground" />
          <span className="sr-only">Checking membership status</span>
        </div>
      );
    }

    if ((inactiveMembership ?? 0) > 0) {
      return <NoMembershipScreen />;
    }

    if (isSuperAdmin) {
      return <NoMembershipScreen />;
    }

    return <Navigate to="/admin/onboarding" replace />;
  }

  if (phase.kind === "picking") {
    return (
      <RestaurantPicker memberships={phase.memberships} onSelect={handlePick} />
    );
  }

  if (!activeRestaurantId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Spinner className="text-muted-foreground" />
        <span className="sr-only">Preparing workspace</span>
      </div>
    );
  }

  // Redirect cashier/kitchen/staff to their default route only when /admin was opened
  // from an auth entry (sign-in, callback, onboarding). "Leave POS" links to /admin
  // without this flag so those users can use the dashboard and other allowed routes.
  const applyRoleLanding = Boolean(
    (locationState as AdminNavigationState | null)?.applyRoleDefaultLanding
  );

  if (!isSuperAdmin && activeRole && applyRoleLanding) {
    const landing = getDefaultLandingForRole(activeRole);

    const normalizedPath = pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;

    if (normalizedPath === "/admin" && landing !== "/admin") {
      return <Navigate to={landing} replace />;
    }
  }

  return (
    <FontProvider>
      <Outlet />
    </FontProvider>
  );
}
